import { Link } from "react-router-dom";
import { insightArticles } from "../content";
import { ArrowIcon } from "../components/Icons";
import Seo from "../components/Seo";
import { assetUrl } from "../asset-url";

const pages = {
  "insights-articles": {
    label: "Insights / Articles",
    title: "Articles and perspectives.",
    lead: "Scientific viewpoints, technical explainers, platform updates, and application stories.",
    image: assetUrl("/assets/optimized/insight-dna.webp"),
  },
  "insights-resources": {
    label: "Insights / Resources",
    title: "Resources for deeper technical understanding.",
    lead: "Public explainers, briefing guides, technical-document pathways, and controlled-access scientific material.",
    image: assetUrl("/assets/optimized/lab-data.webp"),
  },
  "insights-events": {
    label: "Insights / Events",
    title: "Conversations that shape the future.",
    lead: "Scientific forums, platform briefings, product webinars, partnership sessions, and on-demand discussions.",
    image: assetUrl("/assets/optimized/insights-team.webp"),
  },
  "about-leadership": {
    label: "About / Leadership",
    title: "Leadership across science, technology, and translation.",
    lead: "Medein’s leadership model connects scientific review, platform direction, products, operations, governance, and partnerships.",
    image: assetUrl("/assets/optimized/insights-team.webp"),
  },
  "about-story": {
    label: "About / Our Story",
    title: "From complex biology to coherent systems.",
    lead: "The Medein story is a progression from scientific questions to connected platforms, product lines, and a scalable ecosystem.",
    image: assetUrl("/assets/optimized/lab-branded.webp"),
  },
};

function Hero({ page, pageKey }) {
  return (
    <section className={`inner-hero editorial-hero editorial-hero-${pageKey}`} id="overview">
      <div className="inner-hero-copy">
        <p className="section-label">{page.label}</p>
        <h1>{page.title}</h1>
        <p className="inner-lead">{page.lead}</p>
      </div>
      <div className="inner-hero-media">
        <img src={page.image} alt={`${page.title} — Medein`} fetchPriority="high" decoding="async" />
      </div>
    </section>
  );
}

export default function EditorialPage({ pageKey }) {
  const page = pages[pageKey];

  if (!page) return null;

  return (
    <>
      <Seo title={page.title} description={page.lead} />
      <Hero page={page} pageKey={pageKey} />

      {pageKey === "insights-articles" ? (
        <section className="section editorial-news-index" id="article-index">
          <article className="editorial-feature-story">
            <img src={insightArticles[0].image} alt="" loading="lazy" decoding="async" />
            <div>
              <span>{insightArticles[0].category}</span>
              <h2>{insightArticles[0].title}</h2>
              <p>{insightArticles[0].summary}</p>
              <Link className="text-link" to={`/insights/articles/${insightArticles[0].slug}`}>
                Open article <ArrowIcon />
              </Link>
            </div>
          </article>
          <aside className="editorial-story-rail">
            <p className="section-label">Latest perspectives</p>
            {insightArticles.slice(1).map((article) => (
              <article key={article.slug}>
                <span>{article.category}</span>
                <h3>{article.title}</h3>
                <p>{article.summary}</p>
                <Link className="text-link" to={`/insights/articles/${article.slug}`}>
                  Open article <ArrowIcon />
                </Link>
              </article>
            ))}
          </aside>
        </section>
      ) : null}

      {pageKey === "insights-articles" && insightArticles.length > 3 ? (
        <section className="section utility-insights">
          {insightArticles.slice(3).map((article) => (
            <article key={article.slug}>
              <img src={article.image} alt="" loading="lazy" decoding="async" />
              <div>
                <span>{article.category}</span>
                <h2>{article.title}</h2>
                <p>{article.summary}</p>
                <Link className="text-link" to={`/insights/articles/${article.slug}`}>Open article <ArrowIcon /></Link>
              </div>
            </article>
          ))}
        </section>
      ) : null}

      {pageKey === "insights-resources" ? (
        <>
          <section className="section context-section">
            <div className="section-heading split-heading">
              <div><p className="section-label">Resource types</p><h2>Use the access path that matches the evidence.</h2></div>
              <p>Public resources explain architecture and governance. Specifications, validation records, and sensitive briefs require qualification and may require an NDA.</p>
            </div>
            <div className="context-grid">
              {[
                ["Platform overview", "A public guide to Medein’s connected science and platform architecture."],
                ["Evidence governance guide", "How content status, evidence level, limitations, and access are reviewed."],
                ["Partnership briefing checklist", "What to prepare for a focused, non-confidential program discussion."],
                ["Controlled technical documents", "Program- or product-specific material shared after qualification."],
              ].map(([title, text], index) => (
                <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </section>
          <section className="detail-cta">
            <div><p className="section-label">Access</p><h2>Request the right level of technical material.</h2><p>Tell us your organization, intended context, and the evidence needed for your review.</p></div>
            <Link className="button button-light" to="/contact">Request access <ArrowIcon /></Link>
          </section>
        </>
      ) : null}

      {pageKey === "insights-events" ? (
        <>
          <section className="section context-section">
            <div className="section-heading split-heading">
              <div><p className="section-label">Event formats</p><h2>Focused conversations, reviewed content.</h2></div>
              <p>Event announcements include topic, format, timezone, audience, speakers, registration status, and replay availability when approved.</p>
            </div>
            <div className="context-grid">
              {[
                ["Scientific forums", "Evidence, measurement, and biological-design discussions."],
                ["Platform briefings", "Module architecture, integration, and partnership pathways."],
                ["Product webinars", "Status-aware product and application information."],
                ["Partner sessions", "Non-confidential collaboration and diligence preparation."],
              ].map(([title, text], index) => (
                <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </section>
          <section className="section job-section">
            <div><p className="section-label">Upcoming</p><h2>Public schedule</h2></div>
            <div className="empty-jobs"><p>No public events are currently scheduled.</p><p>Request updates to receive future event and replay announcements.</p><Link className="button button-secondary" to="/contact">Request event updates <ArrowIcon /></Link></div>
          </section>
        </>
      ) : null}

      {pageKey === "about-leadership" ? (
        <>
          <section className="section content-intro-grid">
            <div><p className="section-label">Leadership model</p><h2>Decisions connect domain expertise and governance.</h2></div>
            <p>Medein will publish named executive and advisory profiles only after company approval. This public page presents the functions that guide the organization without inventing individual identities.</p>
          </section>
          <section className="section context-section">
            <div className="context-grid">
              {[
                ["Science & Translation", "Scientific direction, evidence standards, and translational review."],
                ["Platform & AI", "Architecture, data systems, model governance, and technical delivery."],
                ["Products & Operations", "Portfolio status, quality, manufacturing, and execution."],
                ["Partnerships & Governance", "External collaboration, responsible access, and oversight."],
              ].map(([title, text], index) => (
                <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </section>
          <section className="governance-callout">
            <div><p className="section-label">Governance</p><h2>Clarity of ownership.</h2></div>
            <p>Public content, scientific evidence, product status, controlled access, and external statements each require a named owner and review path.</p>
            <Link className="text-link" to="/contact">Media and company contact <ArrowIcon /></Link>
          </section>
        </>
      ) : null}

      {pageKey === "about-story" ? (
        <>
          <section className="section editorial-copy">
            <div><p className="section-label">Origin</p><h2>A question: can complex biology become a coherent design system?</h2></div>
            <div><p>Medein begins from the belief that biological complexity should be approached with connected evidence, explicit interfaces, and precision measurement.</p><p>The company architecture links foundational science to reusable platform modules, focused applications, product systems, and transparent pipeline programs.</p></div>
          </section>
          <section className="overview-flow">
            <div><p className="section-label">Evolution</p><h2>From capability to ecosystem.</h2></div>
            <ol>
              {["Science questions", "Platform modules", "Application programs", "Product systems", "Partner ecosystem"].map((step, index) => (
                <li key={step}><span>{String(index + 1).padStart(2, "0")}</span><strong>{step}</strong></li>
              ))}
            </ol>
          </section>
          <section className="section context-section">
            <div className="context-grid">
              {[
                ["Medein", "A brand centered on making, designing, and translating with responsibility."],
                ["Signal mark", "The visual system connects biological signal, measurement, and programmable form."],
                ["Programmable Life", "A direction for working with biological systems through modular design."],
                ["Precision Science", "A commitment to evidence, measurement, review, and clear limitations."],
              ].map(([title, text], index) => (
                <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </>
  );
}
